import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Card } from 'react-bootstrap';
import { FaFileInvoiceDollar } from 'react-icons/fa';
import { API_ENDPOINTS } from '../config/api';

// Función auxiliar para formatear moneda
const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(Number(amount))) return '$0.00';
    // Asegura que amount sea un número antes de llamar a toFixed
    const numAmount = Number(amount);
    return `$${numAmount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
};

// Función auxiliar para formatear fecha
const formatDateDisplay = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        // Intenta detectar si es solo fecha (YYYY-MM-DD) para evitar problemas de timezone
        if (dateString.length === 10 && dateString.includes('-')) {
           const parts = dateString.split('-');
           // Nota: Crear fecha UTC para evitar corrimientos de día por zona horaria local
           const utcDate = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
           if (isNaN(utcDate.getTime())) return 'Fecha inválida';
           return utcDate.toLocaleDateString('es-EC', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
        }
        // Para fechas completas (con hora), intenta usar la zona horaria local
        if (isNaN(date.getTime())) return 'Fecha inválida';
        return date.toLocaleDateString('es-EC', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (e) {
        console.error("Error formateando fecha:", dateString, e);
        return 'Fecha inválida';
    }
};

const Pay = ({ show, onHide, payment, onPaymentSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [message, setMessage] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('transfer');
    const [comprobante, setComprobante] = useState(null);
    const [comprobanteNombre, setComprobanteNombre] = useState('');

    // Resetear estados cuando se abre/cierra el modal
    useEffect(() => {
        if (show) {
            setError(null);
            setSuccess(false);
            setMessage('');
            setComprobante(null);
            setComprobanteNombre('');
            setPaymentMethod('transfer');
        }
    }, [show, payment]);

    // Manejar cambio de archivo
    const handleFileChange = (e) => {
        const file = e.target.files ? e.target.files[0] : null;
        if (file) {
            // Validar tamaño (máximo 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setError('El archivo es demasiado grande. Máximo 5MB.');
                setComprobante(null);
                setComprobanteNombre('');
                e.target.value = null;
                return;
            }
            
            // Validar tipo de archivo
            const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
            if (!validTypes.includes(file.type)) {
                setError('Tipo de archivo no válido. Solo JPG, PNG o PDF.');
                setComprobante(null);
                setComprobanteNombre('');
                e.target.value = null;
                return;
            }
            
            setComprobante(file);
            setComprobanteNombre(file.name);
            setError(null);
        } else {
            setComprobante(null);
            setComprobanteNombre('');
        }
    };

    // Manejar envío del formulario
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validaciones básicas
        if (paymentMethod === 'transfer' && !comprobante) {
            setError('Debe seleccionar un archivo de comprobante.');
            return;
        }
        
        if (!payment || !payment.id) {
            setError('Error: No se ha seleccionado un pago válido.');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(false);
        setMessage('Enviando comprobante y marcando para validación...');

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No autenticado. Por favor, inicie sesión de nuevo.');
            }

            // Crear FormData para enviar el archivo
            const formData = new FormData();
            formData.append('comprobante', comprobante);

            // Enviar solicitud al servidor
            const response = await fetch(API_ENDPOINTS.paymentsSubmitApproval(payment.id), {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`
                    // No incluir Content-Type aquí - FormData lo configura automáticamente
                },
                body: formData
            });

            // Verificar el tipo de respuesta antes de intentar parsear como JSON
            const contentType = response.headers.get("content-type");
            let responseData;
            
            if (contentType && contentType.indexOf("application/json") !== -1) {
                responseData = await response.json();
            } else {
                // Si no es JSON, obtener como texto y mostrar error detallado
                const textResponse = await response.text();
                console.error("Respuesta no JSON:", textResponse);
                throw new Error(`El servidor no devolvió JSON válido. Código: ${response.status}`);
            }

            // Si hay error en la respuesta, lanzar excepción
            if (!response.ok) {
                throw new Error(responseData.message || `Error ${response.status}: No se pudo completar la operación.`);
            }

            // Operación exitosa
            setMessage(responseData.message || 'Comprobante enviado exitosamente. Su pago está ahora pendiente de validación.');
            setSuccess(true);

            // Notificar al componente padre
            if (onPaymentSuccess) {
                onPaymentSuccess(responseData.payment ? responseData.payment.id : payment.id);
            }

            // Cerrar el modal después de un tiempo
            setTimeout(() => {
                onHide();
            }, 3000);

        } catch (err) {
            console.error('Error al enviar comprobante:', err);
            setError(err.message || 'Ocurrió un error al enviar el comprobante. Inténtelo de nuevo.');
            setMessage('');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={loading ? null : onHide} backdrop="static" keyboard={!loading} centered>
            <Modal.Header closeButton={!loading} className="bg-primary text-white">
                <Modal.Title>
                    <FaFileInvoiceDollar className="me-2" />
                    Subir Comprobante de Pago
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Mensajes de estado */}
                {loading && !success && (
                    <Alert variant="info" className="d-flex align-items-center">
                        <Spinner animation="border" size="sm" className="me-2" />
                        {message || 'Procesando...'}
                    </Alert>
                )}
                
                {success && (
                    <Alert variant="success">
                        <Alert.Heading>¡Éxito!</Alert.Heading>
                        <p>{message || 'Comprobante enviado. Su pago está pendiente de validación.'}</p>
                    </Alert>
                )}
                
                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible>
                        <strong>Error:</strong> {error}
                    </Alert>
                )}

                {/* Formulario (solo si no hay éxito) */}
                {!success && (
                    <Form onSubmit={handleSubmit} id="paymentUploadForm">
                        {/* Detalles del Pago */}
                        <Card className="mb-3 bg-light border">
                            <Card.Body>
                                <Card.Title as="h6">Detalles del Pago</Card.Title>
                                <p className="mb-1">
                                    <strong>Concepto:</strong> {payment?.concept || 'N/A'}
                                </p>
                                <p className="mb-1">
                                    <strong>Monto:</strong> <span className="text-danger fw-bold">{formatCurrency(payment?.amount)}</span>
                                </p>
                                <p className="mb-0">
                                    <strong>Fecha Límite/Creación:</strong> {formatDateDisplay(payment?.due_date || payment?.created_at)}
                                </p>
                                {payment?.description && (
                                    <p className="mt-1 mb-0"><small><strong>Nota:</strong> {payment.description}</small></p>
                                )}
                            </Card.Body>
                        </Card>

                        {/* Input para subir archivo */}
                        <Form.Group controlId="formFileComprobante" className="mb-3">
                            <Form.Label>
                                <strong>Adjuntar Comprobante</strong> <span className="text-danger">*</span>
                            </Form.Label>
                            <div className="d-flex align-items-center">
                                {!comprobanteNombre && (
                                    <Form.Control
                                        type="file"
                                        name="comprobante"
                                        onChange={handleFileChange}
                                        disabled={loading}
                                        accept=".jpg,.jpeg,.png,.pdf"
                                        required
                                    />
                                )}
                                {comprobanteNombre && (
                                    <div className="d-flex align-items-center justify-content-between p-2 border rounded bg-white w-100">
                                        <span className="text-muted text-truncate me-2" style={{ maxWidth: '80%' }}>
                                            {comprobanteNombre}
                                        </span>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => {
                                                setComprobante(null);
                                                setComprobanteNombre('');
                                                const fileInput = document.getElementById('formFileComprobante');
                                                if(fileInput) fileInput.value = null;
                                            }}
                                            disabled={loading}
                                            aria-label="Quitar archivo"
                                        >
                                            &times;
                                        </Button>
                                    </div>
                                )}
                            </div>
                            <Form.Text className="text-muted">Formatos: JPG, PNG, PDF. Máximo: 5MB.</Form.Text>
                        </Form.Group>

                        {/* Botones de Acción */}
                        <div className="d-flex justify-content-end gap-2 mt-4">
                            <Button variant="secondary" onClick={onHide} disabled={loading}>
                                Cancelar
                            </Button>
                            <Button
                                variant="success"
                                type="submit"
                                form="paymentUploadForm"
                                disabled={loading || !comprobante}
                            >
                                {loading ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2"/>
                                        Enviando...
                                    </>
                                ) : (
                                    'Enviar Comprobante'
                                )}
                            </Button>
                        </div>
                    </Form>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default Pay;